import { createDraftStore, type Stamped } from '../../shared/draft/wizardDraftStore';
import { describeAge, type DraftDescription } from '../../shared/draft/draftDescription';

/**
 * The enterprise /simulate wizard's draft.
 *
 * Everything the wizard knows lived in 14 useState entities in AppContent and was
 * written nowhere, so a reload — or clicking Analysis in the sidebar, or browser Back to
 * '/' which runs resetAll() — silently destroyed the whole study: use case, typed form,
 * audience, segments, the lot (#373, #459, #527).
 *
 * localStorage, not sessionStorage: unlike the marketplace draft this one carries no
 * reusable studyId (the enterprise wizard creates nothing server-side until Run), so
 * there is no risk of re-attaching to a paid study. What it must survive is an
 * accidental tab close, which is the case the field keeps reporting.
 *
 * A restore is always DISCLOSED — see describeDraft and the restore banner. A form that
 * silently opens pre-filled from last week is the failure shape of #376, and the whole
 * point of persisting is to help the user, not to surprise them.
 */

/** Kept structural rather than importing App's Step union, to avoid a circular import. */
export type DraftStep =
  | 'dashboard'
  | 'use-case-form'
  | 'select-audience'
  | 'select-segments'
  | 'review-and-run'
  | 'simulation-results';

export interface SimulateDraft {
  useCaseType: string | null;
  useCaseTitle: string;
  /** Per-use-case editable form state, keyed lowercased exactly as AppContent keys it. */
  formState: Record<string, Record<string, unknown>>;
  savedFormPayload: Record<string, unknown> | null;
  audienceId: number | null;
  audienceName: string;
  segmentIds: number[];
  segmentNames: string[];
  /** JSON turns the numeric segment id into a string key; restore converts it back. */
  personaFilters: Record<string, unknown>;
  dynamicSegmentationData: Record<string, unknown> | null;
  step: DraftStep;
  /** A Set is not JSON — stored as an array and rebuilt on restore. */
  visitedSteps: DraftStep[];
  /**
   * How many images the form actually held when this draft was written.
   *
   * NOT derivable from `imageDraftKey`: every image-capable form emits a key on mount,
   * before the user has picked anything, so keying the "re-upload your images" line on
   * the key's presence made every reload of those forms claim images were missing even
   * when the user had only typed a name.
   */
  imageCount?: number;
}

/** 7 days. Long enough to cover a weekend; short enough that a forgotten study expires. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const simulateDraftStore = createDraftStore<SimulateDraft>({
  namespace: 'bp.simulate.draft.v1',
  storage: 'local',
  maxAgeMs: MAX_AGE_MS,
});

/**
 * Is there enough here to be worth restoring — and to tell the user about?
 *
 * A draft holding only `step: 'dashboard'` describes a user who opened the wizard and
 * did nothing. Restoring that is a no-op the banner would announce for no reason, which
 * trains people to ignore the banner.
 */
export function draftIsWorthRestoring(draft: SimulateDraft | null): draft is SimulateDraft {
  if (!draft?.useCaseType) return false;
  return Boolean(
    filledFormFieldCount(draft) > 0 ||
      draft.audienceId ||
      draft.segmentIds?.length ||
      draft.savedFormPayload,
  );
}

/**
 * Should the wizard restore a stored draft on this render?
 *
 * Extracted from the effect in App.tsx deliberately. This sequence of guards is the part
 * of draft restore that has actually broken, twice, and both times the regression test I
 * wrote asserted the ORDER OF LINES in App.tsx's source — which is true, unfalsifiable,
 * and caught neither. A mutation audit disabled the tile-pick guard below and the whole
 * 4,500-test suite stayed green. Only the browser ever caught it.
 *
 * As a pure function each guard can be individually disabled and a test will fail, which
 * is the only property that makes a regression test worth having.
 *
 * `loadDraft` is a thunk, not a value, because "storage is not even read until the guards
 * pass" is itself part of the contract: reading before `userId` resolves reads the
 * UNSCOPED key, which on a shared machine is the previous account's study.
 */
export type RestoreDecision =
  /** Not our turn — do NOT mark restore attempted; a later render may still restore. */
  | 'skip'
  /** Nothing to restore, but the attempt is over: the save gate must open. */
  | 'settle'
  | 'restore';

export interface RestoreInput {
  pathname: string;
  alreadyAttempted: boolean;
  userId: number | string | null | undefined;
  selectedUseCaseType: string | null;
  loadDraft: () => Stamped<SimulateDraft> | null;
}

export type SaveDecision = 'clear' | 'skip' | 'save';

/**
 * Whether the mirror may write this candidate, must clear the store, or should do nothing.
 *
 * Extracted for the same reason as `decideSimulateRestore` below: interleaved with the
 * mirror's six state reads there is nothing a test can reach except the file's text, and a
 * round-2 review proved it — reverting App.tsx's teardown wiring to its pre-fix state left
 * all 12 tests of the fix GREEN, because they only exercised pure helpers that cannot observe
 * WHEN App.tsx calls them.
 */
export function decideSimulateSave(
  candidate: SimulateDraft,
  submittedUseCases: ReadonlySet<string>,
): SaveDecision {
  // A study sitting on the results screen must not be written back. Restoring one produced
  // "We brought your study back" over "No simulation results to display", because
  // currentSimulationId is deliberately not persisted.
  if (candidate.step === 'simulation-results') return 'clear';

  // And neither must a study that has been SUBMITTED but is being viewed from another step.
  // This is the gap the terminal-step guard cannot see: handleSimulationComplete marks the
  // use case and clears the store, then "Back to review" moves the step to 'review-and-run',
  // so the guard above stops firing and the finished study is re-persisted — now WITH its
  // formState, which is retained on purpose so the post-run Edit works. A reload then
  // restored that formState while `submittedUseCases` came back empty, so the next tile pick
  // handed the completed study's inputs and photos to the new study. That is #376 again,
  // reached through the one state neither guard covered.
  const key = (candidate.useCaseType || '').toLowerCase();
  if (key && submittedUseCases.has(key)) return 'clear';

  if (!draftIsWorthRestoring(candidate)) return 'skip';
  return 'save';
}

export function decideSimulateRestore(input: RestoreInput): {
  decision: RestoreDecision;
  draft: Stamped<SimulateDraft> | null;
} {
  const { pathname, alreadyAttempted, userId, selectedUseCaseType, loadDraft } = input;

  // Only inside the simulate flow. Restoring on '/' repopulated formState behind
  // resetAll() with nothing disclosing it, so the next tile click opened a pre-filled
  // form for a study the user never chose to resume (#376).
  if (pathname !== '/simulate') return { decision: 'skip', draft: null };
  if (alreadyAttempted) return { decision: 'skip', draft: null };
  // Before the user resolves, the draft key is unscoped — see the store's docstring.
  if (!userId) return { decision: 'skip', draft: null };

  // A live selection outranks anything on disk. Without this, clicking a tile raced the
  // restore and the user watched their fresh pick be replaced by an old study.
  if (selectedUseCaseType) return { decision: 'settle', draft: null };

  const draft = loadDraft();
  if (!draftIsWorthRestoring(draft)) return { decision: 'settle', draft: null };
  return { decision: 'restore', draft };
}

/**
 * Fields the user actually typed.
 *
 * Deliberately NOT "any non-empty value". Every form emits its whole shape on mount,
 * defaults included, so an untouched survey form reports
 * `{simName:'', goal:'', ..., useInternalDebate:false, useTwoPassParsing:true,
 * parsedQuestions:[]}`. Counting truthiness made the three booleans read as three filled
 * fields, so merely opening a form persisted a draft and the next visit announced "We
 * brought your study back" listing nothing — the banner-as-noise failure this file's
 * docstring warns about.
 *
 * Content is a non-empty string or a non-empty array. Booleans are toggles with defaults,
 * not work: flipping one and typing nothing is not a study worth resurrecting.
 * (Same defect the build-audience draft had with its three blank competitor slots.)
 */
function filledFormFieldCount(draft: SimulateDraft): number {
  const form = draft.formState?.[(draft.useCaseType || '').toLowerCase()];
  if (!form) return 0;
  return Object.entries(form).filter(([key, v]) => {
    if (key === 'imageDraftKey') return false; // plumbing, not something the user typed
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'boolean') return false; // a toggle is not typed work
    return v != null; // numbers/objects a form genuinely set
  }).length;
}

export type { DraftDescription };

/**
 * What the banner says. Generated from the draft rather than hardcoded: a draft with no
 * images must not claim images are missing, and a same-sitting reload must not say
 * "yesterday". Both would teach the user the banner is noise.
 */
export function describeDraft(
  draft: SimulateDraft & { updatedAt: number },
  now: number,
  imagesAvailable: boolean,
): DraftDescription {
  const form = draft.formState?.[(draft.useCaseType || '').toLowerCase()] || {};
  const restored: string[] = [];

  if (form.simName) restored.push('simulation name');
  if (form.brand) restored.push('brand');
  if (form.goal) restored.push('goal');

  const questions = Array.isArray(form.parsedQuestions) ? form.parsedQuestions.length : 0;
  if (questions) restored.push(`${questions} question${questions === 1 ? '' : 's'}`);
  else if (form.questionsText) restored.push('questions');

  if (draft.audienceName) restored.push(draft.audienceName);
  else if (draft.audienceId) restored.push('audience');

  return {
    age: describeAge(Math.max(0, now - draft.updatedAt)),
    restored,
    // Only when the draft actually HELD images and the in-memory store no longer has
    // them (a reload, not a Back). The count is what distinguishes "had images" from
    // "emitted a key on mount"; see SimulateDraft.imageCount.
    imagesNeedReupload: (draft.imageCount ?? 0) > 0 && !imagesAvailable,
  };
}
